import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add,
  DeleteOutline,
  Logout,
  PhoneAndroid,
  QrCode2,
  Refresh,
  RestoreFromTrash,
  Wifi,
  WifiOff
} from '@mui/icons-material';
import { aziendeApi, whatsappApi } from '../../services/api';
import useWhatsApp from '../../hooks/useWhatsApp';

function statusColor(status) {
  switch (status) {
    case 'ready': return 'success';
    case 'qr_pending': return 'warning';
    case 'authenticated': return 'info';
    case 'error': return 'error';
    default: return 'default';
  }
}

function statusLabel(status) {
  switch (status) {
    case 'ready': return 'Connesso';
    case 'qr_pending': return 'In attesa QR';
    case 'authenticated': return 'Caricamento chat';
    case 'error': return 'Errore di connessione';
    case 'disconnected': return 'Disconnesso';
    default: return status || 'Disconnesso';
  }
}

export default function WhatsappSetup({ embedded = false }) {
  const { lines: socketLines, lineQr, error: socketError, refreshLines: refreshConnectionLines } = useWhatsApp();
  const [lines, setLines] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [newLabel, setNewLabel] = useState('');
  const [newAziendaId, setNewAziendaId] = useState('');
  const [loadingLineId, setLoadingLineId] = useState(null);
  const [savingLineId, setSavingLineId] = useState(null);
  const [deletingLineId, setDeletingLineId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [polledQr, setPolledQr] = useState({});
  const [hiddenContacts, setHiddenContacts] = useState([]);
  const [loadingHiddenContacts, setLoadingHiddenContacts] = useState(false);
  const [restoringPhone, setRestoringPhone] = useState(null);
  const pollRef = useRef(null);

  const aziendeById = useMemo(() => new Map(aziende.map((azienda) => [Number(azienda.id), azienda])), [aziende]);

  const mergedLines = useMemo(() => {
    const byId = new Map();
    for (const line of lines) byId.set(Number(line.id), line);
    for (const line of socketLines || []) {
      if (lines.length && !byId.has(Number(line.id))) continue;
      byId.set(Number(line.id), { ...(byId.get(Number(line.id)) || {}), ...line });
    }
    return [...byId.values()].sort((a, b) => Number(b.is_default) - Number(a.is_default) || Number(a.id) - Number(b.id));
  }, [lines, socketLines]);

  const refreshLines = async () => {
    setLines(await refreshConnectionLines());
  };

  const refreshAziende = async () => {
    const res = await aziendeApi.getAll();
    setAziende(res.data?.data || []);
  };

  const refreshHiddenContacts = async () => {
    setLoadingHiddenContacts(true);
    try {
      const res = await whatsappApi.getHiddenContacts();
      setHiddenContacts(res.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingHiddenContacts(false);
    }
  };

  useEffect(() => {
    refreshLines().catch(() => {});
    refreshAziende().catch(() => {});
    refreshHiddenContacts().catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startQrPolling = (lineId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const [statusRes, qrRes] = await Promise.all([
          whatsappApi.getStatus(lineId),
          whatsappApi.getQr(lineId)
        ]);
        const status = statusRes.data?.data?.status;
        if (status === 'ready') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPolledQr(prev => ({ ...prev, [lineId]: null }));
          await refreshLines();
          return;
        }
        const qrData = qrRes.data?.data?.qr;
        setPolledQr(prev => ({ ...prev, [lineId]: status === 'qr_pending' ? (qrData || null) : null }));
        await refreshLines();
        if (status === 'error' || status === 'disconnected') {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (_) {}
    }, 3000);
  };

  const handleCreateLine = async () => {
    setCreating(true);
    setError(null);
    try {
      await whatsappApi.createLine({
        label: newLabel.trim() || 'WhatsApp',
        azienda_id: newAziendaId || null
      });
      setNewLabel('');
      setNewAziendaId('');
      await refreshLines();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateLineAzienda = async (lineId, aziendaId) => {
    setSavingLineId(lineId);
    setError(null);
    try {
      await whatsappApi.updateLine(lineId, { azienda_id: aziendaId || null });
      await refreshLines();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSavingLineId(null);
    }
  };

  const handleDeleteLine = async (line) => {
    if (mergedLines.length <= 1) {
      setError('Deve rimanere almeno una linea WhatsApp attiva. Aggiungi prima un\'altra linea.');
      return;
    }

    const lineLabel = line.label || `Linea ${line.id}`;
    if (!window.confirm(`Eliminare ${lineLabel}?\nLo storico messaggi resta salvato, ma la linea non sarà più utilizzabile.`)) return;

    setDeletingLineId(line.id);
    setError(null);
    try {
      await whatsappApi.deleteLine(line.id);
      setPolledQr((prev) => {
        const next = { ...prev };
        delete next[line.id];
        return next;
      });
      await refreshLines();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeletingLineId(null);
    }
  };

  const handleRestoreHiddenContact = async (contact) => {
    const phone = contact.phone_number;
    if (!phone) return;

    setRestoringPhone(phone);
    setError(null);
    try {
      await whatsappApi.unhideContact(phone);
      await refreshHiddenContacts();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setRestoringPhone(null);
    }
  };

  const getAziendaLabel = (azienda) => aziendeById.get(Number(azienda?.id))?.company_name || azienda?.company_name || `Azienda ${azienda?.id}`;

  const formatHiddenDate = (value) => {
    if (!value) return null;
    try {
      return new Date(value).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch (_) {
      return null;
    }
  };

  const handleInitialize = async (lineId) => {
    setLoadingLineId(lineId);
    setError(null);
    setPolledQr(prev => ({ ...prev, [lineId]: null }));
    try {
      await whatsappApi.initialize(lineId);
      startQrPolling(lineId);
      await refreshLines();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingLineId(null);
    }
  };

  const handleLogout = async (line) => {
    if (!window.confirm(`Disconnettere ${line.label || 'questa linea'}?`)) return;
    setLoadingLineId(line.id);
    setError(null);
    try {
      await whatsappApi.logout(line.id);
      await refreshLines();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingLineId(null);
    }
  };

  const handleRestart = async (line) => {
    setLoadingLineId(line.id);
    setError(null);
    try {
      await whatsappApi.restart(line.id);
      startQrPolling(line.id);
      await refreshLines();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingLineId(null);
    }
  };

  return (
    <Box sx={{ p: embedded ? { xs: 2, sm: 3 } : 3, maxWidth: embedded ? 'none' : 1000, mx: embedded ? 0 : 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Typography variant={embedded ? 'h6' : 'h4'} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneAndroid /> Configurazione WhatsApp
        </Typography>
        <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            size="small"
            label="Nome linea"
            value={newLabel}
            onChange={(event) => setNewLabel(event.target.value)}
            sx={{ minWidth: 180, flex: { xs: 1, sm: 'initial' } }}
          />
          <FormControl size="small" sx={{ minWidth: 210, flex: { xs: 1, sm: 'initial' } }}>
            <InputLabel>Azienda</InputLabel>
            <Select
              value={newAziendaId}
              label="Azienda"
              onChange={(event) => setNewAziendaId(event.target.value)}
            >
              <MenuItem value="">Nessuna</MenuItem>
              {aziende.map((azienda) => (
                <MenuItem key={azienda.id} value={azienda.id}>{getAziendaLabel(azienda)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={creating ? <CircularProgress size={18} /> : <Add />} onClick={handleCreateLine} disabled={creating}>
            Aggiungi
          </Button>
        </Stack>
      </Stack>

      {(error || socketError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || socketError}
        </Alert>
      )}

      <Stack spacing={2}>
        {mergedLines.map((line) => {
          const qr = line.status === 'qr_pending' ? (lineQr?.[line.id] || polledQr[line.id] || null) : null;
          const loading = loadingLineId === line.id;
          const status = line.status || 'disconnected';
          return (
            <Card key={line.id} variant="outlined">
              <CardContent>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                  <Box sx={{ minWidth: 240 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6">{line.label || `Linea ${line.id}`}</Typography>
                      {line.is_default && <Chip size="small" label="Predefinita" />}
                    </Stack>
                    <Stack spacing={1}>
                      <Chip
                        icon={status === 'ready' ? <Wifi /> : <WifiOff />}
                        label={statusLabel(status)}
                        color={statusColor(status)}
                        variant={status === 'ready' ? 'filled' : 'outlined'}
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      {(line.error || line.last_error) && <Alert severity="error">{line.error || line.last_error}</Alert>}
                      {line.connected_phone && (
                        <Typography variant="body2" color="text.secondary">Numero: {line.connected_phone}</Typography>
                      )}
                      <FormControl size="small" sx={{ maxWidth: 320 }}>
                        <InputLabel>Azienda</InputLabel>
                        <Select
                          value={line.azienda_id || ''}
                          label="Azienda"
                          onChange={(event) => handleUpdateLineAzienda(line.id, event.target.value)}
                          disabled={savingLineId === line.id}
                        >
                          <MenuItem value="">Nessuna</MenuItem>
                          {aziende.map((azienda) => (
                            <MenuItem key={azienda.id} value={azienda.id}>{getAziendaLabel(azienda)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>

                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    {status === 'authenticated' && (
                      <Stack spacing={1} alignItems="center">
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">Collegamento in completamento…</Typography>
                      </Stack>
                    )}
                    {(qr || status === 'qr_pending') && (
                      <Box sx={{ display: 'inline-block', p: 1.5, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        {qr ? (
                          <Box component="img" src={qr} alt={`QR ${line.label || line.id}`} sx={{ width: 220, height: 220, display: 'block' }} />
                        ) : (
                          <Box sx={{ width: 220, height: 220, display: 'grid', placeItems: 'center' }}>
                            <CircularProgress />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                    {status !== 'ready' && (
                      <Tooltip title="Connetti linea">
                        <span>
                          <Button variant="contained" color="success" startIcon={loading ? <CircularProgress size={18} /> : <QrCode2 />} onClick={() => handleInitialize(line.id)} disabled={loading}>
                            Connetti
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Riavvia linea">
                      <span>
                        <Button variant="outlined" startIcon={<Refresh />} onClick={() => handleRestart(line)} disabled={loading}>
                          Riavvia
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Disconnetti linea">
                      <span>
                        <Button variant="outlined" color="error" startIcon={<Logout />} onClick={() => handleLogout(line)} disabled={loading || status !== 'ready'}>
                          Esci
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={mergedLines.length <= 1 ? 'Aggiungi un\'altra linea prima di eliminare questa' : 'Elimina linea'}>
                      <span>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteLine(line)}
                          disabled={deletingLineId === line.id || mergedLines.length <= 1}
                          size="small"
                        >
                          {deletingLineId === line.id ? <CircularProgress size={20} /> : <DeleteOutline />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}

        {mergedLines.length === 0 && (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2} alignItems="flex-start">
                <Typography variant="h6">Nessuna linea configurata</Typography>
                <Divider flexItem />
                <Button variant="contained" startIcon={<Add />} onClick={handleCreateLine} disabled={creating}>
                  Crea linea WhatsApp
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Contatti esclusi dalla sincronizzazione</Typography>
          <Typography variant="body2" color="text.secondary">
            Sono i contatti nascosti dalla tab WhatsApp.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={loadingHiddenContacts ? <CircularProgress size={18} /> : <Refresh />} onClick={refreshHiddenContacts} disabled={loadingHiddenContacts}>
          Aggiorna
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          {loadingHiddenContacts ? (
            <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : hiddenContacts.length === 0 ? (
            <Typography color="text.secondary">Nessun contatto escluso.</Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {hiddenContacts.map((contact) => {
                const dateLabel = formatHiddenDate(contact.created_at);
                return (
                  <Stack key={contact.id || contact.phone_number} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ py: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {contact.contact_name || contact.phone_number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {contact.phone_number}{dateLabel ? ` · escluso il ${dateLabel}` : ''}
                      </Typography>
                      {contact.reason && (
                        <Typography variant="caption" color="text.secondary">
                          Motivo: {contact.reason}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title="Ripristina nella lista chat">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={restoringPhone === contact.phone_number ? <CircularProgress size={16} /> : <RestoreFromTrash />}
                          onClick={() => handleRestoreHiddenContact(contact)}
                          disabled={restoringPhone === contact.phone_number}
                        >
                          Ripristina
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}